"use client";

import { memo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Copy, ExternalLink } from "lucide-react";
import type { ActivityEvent } from "../types";
import { useT } from "@/hooks/useT";
import { fmtCents } from "@/lib/format-price";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { marketPageUrl } from "@/lib/source-url";
import { activityProfileHref } from "@/lib/activity-link";
import { formatUSD, timeAgo, displayTraderName, abbreviateOutcomeLabel } from "../helpers/format";
import CdnImage from "@/components/CdnImage";
import UserHoverCard from "@/app/components/UserHoverCard";
import GradientAvatar from "@/app/components/GradientAvatar";

/**
 * CompactEventRow — single dense row for the "compact" view of the
 * activity feed. The expanded view goes through `ActivityEventCard`
 * (which dispatches per event_type to a tall card); this row collapses
 * every event type into one horizontal strip.
 *
 * Memo'd to skip re-renders during 30s background polls. Default
 * shallow equality is sufficient because handleVote returns the same
 * `e` reference for unchanged events (only the mutated event gets a
 * fresh object), and onCopy / onTrade are useCallback'd in the parent.
 */
const CompactEventRow = memo(function CompactEventRow({
  event,
  index,
  onCopy,
  onTrade,
}: {
  event: ActivityEvent;
  index: number;
  onCopy: (event: ActivityEvent) => void;
  onTrade: (event: ActivityEvent) => void;
}) {
  void onTrade;
  const t = useT();
  const router = useRouter();
  const isTrade = ["user_trade", "copy_trade", "whale_trade", "tracker_trade", "user_post_trade"].includes(event.event_type);
  const isMarketMove = event.event_type === "market_move";
  const isAutoEvent = ["market_move", "new_market", "new_event", "resolution"].includes(event.event_type);
  const isSell = event.side === "SELL";
  const isYes = event.outcome?.toLowerCase().includes("yes") || event.outcome?.toLowerCase().includes("up");
  const isPump = event.event_subtype === "pump";

  const displayName = isAutoEvent
    ? "FrenFlow AI"
    : displayTraderName(
        event.metadata?.display_name
        || event.source_username
        || event.source_wallet
      ) || "User";

  const getActionLabel = () => {
    if (event.event_type === "new_market" || event.event_type === "new_event") return "NEW";
    if (event.event_type === "market_move") return event.event_subtype === "pump" ? "PUMP" : "DUMP";
    if (event.event_type === "resolution") return "RESOLVED";
    if (event.event_type === "follow") return "FOLLOW";
    if (event.event_type === "user_post_opinion") return "POST";
    if (event.event_type === "copy_trade") return "COPY";
    if (event.event_type === "pnl_win") return "WIN";
    if (event.event_type === "pnl_loss") return "LOSS";
    if (event.event_type === "blog_article") return "ARTICLE";
    return isSell ? "SELL" : "BUY";
  };

  const actionLabel = getActionLabel();
  const actionColor = isSell || actionLabel === "LOSS" || actionLabel === "DUMP"
    ? "text-red-500"
    : actionLabel === "ARTICLE"
    ? "text-sky-500"
    : actionLabel === "NEW" || actionLabel === "RESOLVED" || actionLabel === "FOLLOW" || actionLabel === "POST"
    ? "text-gray-400"
    : "text-emerald-500";

  const marketTitle = event.market_title ? decodeHTMLEntities(event.market_title) : event.headline;
  const hasMarket = !!event.market_slug;
  const isBlogArticle = event.event_type === "blog_article";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      onClick={() => router.push(isBlogArticle && event.metadata?.slug ? `/blog/${event.metadata.slug}` : `/activity/${event.id}`)}
      className="group relative border-b border-gray-100 dark:border-[#1A1A1A]/50 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors"
    >
      {/* ===== DESKTOP layout ===== */}
      <div className="hidden md:flex items-center gap-2.5 px-3 py-2">
        {/* Market image */}
        {event.market_image ? (
          <div className="w-7 h-7 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-[#0f0f12]">
            <CdnImage src={event.market_image} alt="" width={28} height={28} sizes="28px" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-7 h-7 rounded-[5px] flex-shrink-0 bg-gray-200 dark:bg-[#0f0f12]" />
        )}

        {/* Avatar — checks both user_image (social posts) AND
            wallet_profile_image (whale trades), then falls back to a
            deterministic GradientAvatar so the slot is never blank. */}
        <div className="w-6 h-6 rounded-[5px] flex-shrink-0 overflow-hidden">
          {isAutoEvent ? (
            <img src="/brand/frenflow_logo.png" alt="FrenFlow AI" width={24} height={24} className="w-full h-full rounded-[5px] object-cover" />
          ) : event.metadata?.user_image ? (
            <CdnImage src={event.metadata.user_image} alt={String(displayName)} width={24} height={24} sizes="24px" className="w-full h-full rounded-[5px] object-cover" />
          ) : event.metadata?.wallet_profile_image ? (
            <CdnImage src={event.metadata.wallet_profile_image} alt={String(displayName)} width={24} height={24} sizes="24px" className="w-full h-full rounded-[5px] object-cover" />
          ) : (
            <GradientAvatar seed={String(displayName)} size={24} />
          )}
        </div>

        {/* Username */}
        {isTrade && !isAutoEvent ? (
          event.event_type === "copy_trade" ? (
            <div className="flex items-baseline gap-1 truncate w-32 flex-shrink-0">
              <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
                <Link
                  href={`/profile/${event.source_username || "anonymous"}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-bold text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors truncate"
                >
                  {displayName}
                </Link>
              </UserHoverCard>
              <span className="text-[10px] text-gray-400 flex-shrink-0">&rarr;</span>
              <UserHoverCard username={event.metadata?.leader_username || undefined} walletAddress={event.metadata?.leader_address || undefined}>
                <Link
                  href={event.metadata?.leader_address ? `/traders/@${event.metadata.leader_address}` : `/profile/${event.metadata?.leader_username || "anonymous"}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs font-bold text-emerald-500 hover:text-emerald-400 transition-colors truncate"
                >
                  <img src={event.metadata?.leader_image || '/frenpfp.png'} alt="" className="w-3.5 h-3.5 rounded-[5px] object-cover" />
                  @{event.metadata?.leader_username || "trader"}
                </Link>
              </UserHoverCard>
            </div>
          ) : (
            <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
              <Link
                href={activityProfileHref(event)}
                onClick={(e) => e.stopPropagation()}
                className="text-xs font-bold text-gray-900 dark:text-white truncate w-24 flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {displayName}
              </Link>
            </UserHoverCard>
          )
        ) : (
          <span className="text-xs font-bold text-gray-900 dark:text-white truncate w-24 flex-shrink-0">
            {displayName}
          </span>
        )}

        {/* Action + Amount + Outcome + Price pill — amount inlined right
            after the verb so the eye reads "bought $275K · YES @ 35¢" as
            one chunk, same prose order as the grid (WhaleTradeRow) cards. */}
        {isTrade ? (
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 dark:bg-[#0f0f12] border rounded-[5px] text-[10px] font-bold flex-shrink-0 ${
            isSell ? "border-red-500/20" : "border-emerald-500/20"
          }`}>
            <div className={`w-1.5 h-1.5 rounded-[5px] ${isSell ? "bg-red-500" : "bg-emerald-500"}`} />
            <span className={isSell ? "text-red-500" : "text-emerald-500"}>{actionLabel}</span>
            {event.size_usd != null && event.size_usd > 0 && (
              <span className="text-gray-900 dark:text-white tabular-nums">{formatUSD(event.size_usd)}</span>
            )}
            <div className={`h-2.5 w-px ${isSell ? "bg-red-500/20" : "bg-emerald-500/20"}`} />
            {event.outcome && (
              <span className={isYes ? "text-emerald-500" : "text-red-500"}>{abbreviateOutcomeLabel(event.outcome)}</span>
            )}
            {event.price != null && (
              <>
                <span className="text-gray-400 dark:text-gray-500">@</span>
                <span className="text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
              </>
            )}
          </div>
        ) : isMarketMove ? (
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 dark:bg-[#0f0f12] border rounded-[5px] text-[10px] font-bold flex-shrink-0 ${
            isPump ? "border-emerald-500/20" : "border-red-500/20"
          }`}>
            <div className={`w-1.5 h-1.5 rounded-[5px] ${isPump ? "bg-emerald-500" : "bg-red-500"}`} />
            <span className={isPump ? "text-emerald-500" : "text-red-500"}>{actionLabel}</span>
            {event.metadata?.price_change_percent != null && (
              <>
                <div className={`h-2.5 w-px ${isPump ? "bg-emerald-500/20" : "bg-red-500/20"}`} />
                <span className={isPump ? "text-emerald-500" : "text-red-500"}>
                  {isPump ? "+" : ""}{Number(event.metadata.price_change_percent || 0).toFixed(1)}%
                </span>
              </>
            )}
            {event.price != null && (
              <>
                <span className="text-gray-400 dark:text-gray-500">→</span>
                <span className="text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
              </>
            )}
          </div>
        ) : (
          <span className={`text-[10px] font-bold flex-shrink-0 ${actionColor}`}>{actionLabel}</span>
        )}

        {/* 2026-05-22 (Pika audit): the previous flex-1 spacer created
            a large empty gap between the action pill and the market
            title when the action pill was short ("FOLLOW", "NEW",
            etc.). Replaced with flex-1 ON THE MARKET TITLE so the
            title takes the remaining space (truncating if needed)
            and the time badge stays glued to the right. Removes the
            "huecos blancos en el medio" Pika reported. */}

        {/* Market title — takes the slack between the action pill
            and the right-aligned time badge. */}
        {marketTitle ? (
          <span className="flex-1 min-w-0 inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-[11px] text-gray-700 dark:text-gray-300">
            <span className="truncate">{marketTitle}</span>
          </span>
        ) : (
          <div className="flex-1 min-w-0" />
        )}

        {/* Trade count + Time (amount moved into the action pill above so
            the right side stays clean). */}
        <div className={`flex items-center gap-2 flex-shrink-0 ${hasMarket ? "group-hover:hidden" : ""}`}>
          {event.metadata?.grouped_trade_count > 1 && (
            <span className="text-[10px] font-bold text-violet-500 dark:text-gray-400">
              ×{event.metadata.grouped_trade_count}
            </span>
          )}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
            {timeAgo(event.event_at, t)}
          </span>
        </div>

        {/* Hover: Copy + View buttons (replace amount+time) */}
        <div className="hidden group-hover:flex items-center gap-1.5 flex-shrink-0">
          {isTrade && (event.source_wallet || event.metadata?.wallet_address) && (
            <button
              onClick={(e) => { e.stopPropagation(); onCopy(event); }}
              className="flex items-center gap-1 px-2 py-1 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[10px] font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copy
            </button>
          )}
          {hasMarket && (
            <Link
              href={event.metadata?.source === 'hyperliquid' || (event.market_slug?.includes('-q-') && event.market_slug?.endsWith('-yes'))
                ? `/hyperliquid/event/${encodeURIComponent(event.market_slug || '')}`
                : event.market_slug?.toLowerCase().startsWith('kx')
                  ? `/market/${event.market_slug}?source=kalshi`
                  : marketPageUrl({ marketSlug: event.market_slug, eventSlug: event.event_slug, outcome: event.outcome, source: (event.metadata?.source as 'polymarket' | 'predict') || 'polymarket', isMultiOutcome: event.is_multi_outcome })}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 px-2.5 py-1 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[10px] font-bold hover:brightness-110 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View
            </Link>
          )}
        </div>
      </div>

      {/* ===== MOBILE layout ===== */}
      <div className="flex md:hidden gap-2.5 px-3 py-2.5">
        {/* Left: stacked avatars (market image + user avatar overlapping) */}
        <div className="relative flex-shrink-0 w-10 h-10">
          {event.market_image ? (
            <div className="w-10 h-10 rounded-[5px] overflow-hidden bg-gray-200 dark:bg-[#0f0f12]">
              <CdnImage src={event.market_image} alt="" width={40} height={40} sizes="40px" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-[5px] bg-gray-200 dark:bg-[#0f0f12]" />
          )}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-[5px] overflow-hidden border-2 border-white dark:border-[#18181D]">
            {isAutoEvent ? (
              <img src="/brand/frenflow_logo.png" alt="FrenFlow AI" width={20} height={20} className="w-full h-full rounded-[5px] object-cover" />
            ) : event.metadata?.user_image ? (
              <CdnImage src={event.metadata.user_image} alt={String(displayName)} width={20} height={20} sizes="20px" className="w-full h-full rounded-[5px] object-cover" />
            ) : event.metadata?.wallet_profile_image ? (
              <CdnImage src={event.metadata.wallet_profile_image} alt={String(displayName)} width={20} height={20} sizes="20px" className="w-full h-full rounded-[5px] object-cover" />
            ) : (
              <GradientAvatar seed={String(displayName)} size={20} />
            )}
          </div>
        </div>

        {/* Right: two rows */}
        <div className="flex-1 min-w-0">
          {/* Row 1: username + action pill + amount + time */}
          <div className="flex items-center gap-1.5 mb-0.5">
            {isTrade && !isAutoEvent ? (
              <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
                <Link
                  href={activityProfileHref(event)}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-bold text-gray-900 dark:text-white truncate max-w-[90px] hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {displayName}
                </Link>
              </UserHoverCard>
            ) : (
              <span className="text-xs font-bold text-gray-900 dark:text-white truncate max-w-[90px]">
                {displayName}
              </span>
            )}
            {isTrade ? (
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-[#0f0f12] border rounded-[5px] text-[10px] font-bold ${
                isSell ? "border-red-500/20" : "border-emerald-500/20"
              }`}>
                <div className={`w-1 h-1 rounded-[5px] ${isSell ? "bg-red-500" : "bg-emerald-500"}`} />
                <span className={isSell ? "text-red-500" : "text-emerald-500"}>{actionLabel}</span>
                <div className={`h-2.5 w-px ${isSell ? "bg-red-500/20" : "bg-emerald-500/20"}`} />
                {event.outcome && (
                  <span className={isYes ? "text-emerald-500" : "text-red-500"}>{abbreviateOutcomeLabel(event.outcome)}</span>
                )}
                {event.price != null && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500">@</span>
                    <span className="text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
                  </>
                )}
              </div>
            ) : isMarketMove ? (
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-[#0f0f12] border rounded-[5px] text-[10px] font-bold ${
                isPump ? "border-emerald-500/20" : "border-red-500/20"
              }`}>
                <div className={`w-1 h-1 rounded-[5px] ${isPump ? "bg-emerald-500" : "bg-red-500"}`} />
                <span className={isPump ? "text-emerald-500" : "text-red-500"}>{actionLabel}</span>
                {event.metadata?.price_change_percent != null && (
                  <>
                    <div className={`h-2.5 w-px ${isPump ? "bg-emerald-500/20" : "bg-red-500/20"}`} />
                    <span className={isPump ? "text-emerald-500" : "text-red-500"}>
                      {isPump ? "+" : ""}{Number(event.metadata.price_change_percent || 0).toFixed(1)}%
                    </span>
                  </>
                )}
                {event.price != null && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500">→</span>
                    <span className="text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
                  </>
                )}
              </div>
            ) : (
              <span className={`text-[10px] font-bold ${actionColor}`}>{actionLabel}</span>
            )}
            <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
              {event.size_usd != null && event.size_usd > 0 && (
                <span className="text-xs font-bold text-gray-900 dark:text-white">{formatUSD(event.size_usd)}</span>
              )}
              {event.metadata?.grouped_trade_count > 1 && (
                <span className="text-[10px] font-bold text-violet-500 dark:text-gray-400">
                  ×{event.metadata.grouped_trade_count}
                </span>
              )}
              <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">{timeAgo(event.event_at, t)}</span>
            </span>
          </div>
          {/* Row 2: market title (full, wrapping allowed) */}
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug line-clamp-2">
            {marketTitle}
          </p>
        </div>
      </div>
    </motion.div>
  );
});

export default CompactEventRow;
